import { AutocompleteOption, LocationAutocomplete } from '@checkit/react-components';
import { FormPanel } from '@checkit/react-components';
import { CancelIcon } from '@checkit/react-components/build/components/Icons';
import { Box, Button, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import { createLocation } from 'history';
import Papa from 'papaparse';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import { CheckpointTypeDto } from '../../checkpointTypes/state';
import { RouteLocationState } from '../../common/WithQueryParams';
import { CheckpointTypeAutocomplete } from '../../components/checkpointTypeAutocomplete/CheckpointTypeAutocomplete';
import MaterialUiLink from '../../components/MaterialUiLink';
import RouteLeavingGuard from '../../components/routeLeaving/routeLeaving';
import { appMessages } from '../../translations';
import useWindowUnload from '../components/form/hooks/usePreventWindowUnload';
import { checkpointSchema } from '../schema';
import { CheckpointDto } from '../state';

import { messages } from './BulkImportCheckpoints.translations';

export const MAX_CSV_LENGTH = 100;

export enum CsvFields {
  name,
  description,
  identifier,
  _LENGTH
}

export enum CsvErrorCodes {
  maxlength,
  nofields,
  uniqname,
  uniqid
}

// Split to allow testing and error formatting
export function findCsvErrorCode(values: string[][]): CsvErrorCodes | null {
  if (values.length > MAX_CSV_LENGTH) {
    return CsvErrorCodes.maxlength;
  }

  if (values.some(row => row.length !== CsvFields._LENGTH)) {
    return CsvErrorCodes.nofields;
  }

  if (new Set(values.map(x => x[CsvFields.name])).size !== values.length) {
    return CsvErrorCodes.uniqname;
  }

  const specifiedIds = values.filter(x => !!x[CsvFields.identifier]);
  if (new Set(specifiedIds.map(x => x[CsvFields.identifier])).size !== specifiedIds.length) {
    return CsvErrorCodes.uniqid;
  }

  return null;
}

const useStyles = makeStyles((theme: Theme) => ({
  errorLabel: {
    marginLeft: theme.spacing(1)
  },
  formInput: {
    marginTop: theme.spacing(0)
  },
  submitButton: {
    justifyContent: 'flex-end',
    margin: theme.spacing(1)
  }
}));

export function arrayToCheckpoint(fields: string[]): CheckpointDto {
  if (fields.length !== CsvFields._LENGTH) {
    throw Error(`arrayToCheckpoint must have ${CsvFields._LENGTH} fields`);
  }

  return {
    description: fields[CsvFields.description] || undefined,
    locationCode: null,
    name: fields[CsvFields.name],
    qrCodeId: fields[CsvFields.identifier] || null,
    sequenceNumber: null,
    types: []
  };
}

export interface Props {
  onImport: (checkpoints: CheckpointDto[]) => void;
}

const BulkImportCheckpointsForm = ({ onImport }: Props) => {
  const [types, setTypes] = useState<CheckpointTypeDto[]>([]);
  const [locationCode, setLocationCode] = useState<string | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [checkpoints, setCheckpoints] = useState<CheckpointDto[]>([]);
  const [dirty, setDirty] = useState(false);

  const { formatMessage } = useIntl();

  const schema = checkpointSchema(formatMessage);

  const typeValue = types.map(type => ({ name: String(type.name), code: String(type.code) }));

  const readyToImport = csvError === null && !!fileName;

  useWindowUnload(dirty);

  const onFormChange = () => setDirty(true);

  const onTypeChange = useCallback((event: React.ChangeEvent<{}>, value: AutocompleteOption[] | null) => {
    setTypes(value || []);
    setDirty(true);
  }, [setTypes]);

  const onLocationChange = useCallback((event: React.ChangeEvent<{}>, value: AutocompleteOption | null) => {
    setLocationCode(value ? value.code : null);
    setDirty(true);
  }, [setLocationCode]);

  const classes = useStyles();

  const previousState = useHistory().location.state as RouteLocationState;
  const previousLocation = previousState ? previousState.previousLocation : createLocation('/checkpoints');

  function findCsvErrorString(values: string[][]): string | null {
    const error = findCsvErrorCode(values);
    switch (error) {
      case CsvErrorCodes.maxlength:
        return formatMessage(messages['checkpoint.bulkImport.error.maxlength'], { MAX_CSV_LENGTH });
      case CsvErrorCodes.nofields:
        return formatMessage(messages['checkpoint.bulkImport.error.nofields'], { LENGTH: CsvFields._LENGTH });
      case CsvErrorCodes.uniqname:
        return formatMessage(messages['checkpoint.bulkImport.error.uniqname']);
      case CsvErrorCodes.uniqid:
        return formatMessage(messages['checkpoint.bulkImport.error.uniqid']);
      case null:
        return null;
      default:
        throw Error('Invalid Value');
    }
  }

  function processCsv(results: Papa.ParseResult<string[]>) {
    const { errors, data: csvData } = results;

    if (errors.length > 0) {
      setCsvError(formatMessage(messages['checkpoint.bulkImport.error.invalidFile']));
      return;
    }

    const csvErrorResult = findCsvErrorString(csvData);
    setCsvError(csvErrorResult);

    if (!csvErrorResult) {
      const checkpointDtos = csvData.map(arrayToCheckpoint);

      for (let index = 0; index < checkpointDtos.length; index++) {
        try {
          schema.validateSync(checkpointDtos[index]);
        } catch (e) {
          setCsvError(formatMessage(messages['checkpoint.bulkImport.error.validation'], {
            error: e.message,
            index: index + 1
          }));

          return;
        }
      }

      setCheckpoints(checkpointDtos);
      setDirty(true);
    }
  }

  function handleImportClick() {
    const updatedCheckpoints = checkpoints.map(checkpoint => {
      return {
        ...checkpoint,
        locationCode,
        types
      };
    });

    onImport(updatedCheckpoints);
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.files) {
      return;
    }
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      Papa.parse(file, { complete: processCsv, skipEmptyLines: true });
    }
  }

  return (
    <form onChange={onFormChange}>
      <FormPanel
        heading={formatMessage(messages['checkpoint.bulkImport.formHeader.csv'])}
        name="checkpoint"
        initialExpanded={true}
      >
        <Box component="div" display="inline">
          <Button
            variant="contained"
            component="label"
            color="primary"
            size="medium"
            className={classes.formInput}
          >
            {formatMessage(messages['checkpoint.bulkImport.formHeader.selectFile'])}
            <input
              type="file"
              style={{ display: 'none' }}
              accept=".csv"
              onChange={handleFileChange}
            />
          </Button>
          <Typography
            variant="subtitle2"
            display="inline"
            className={classes.errorLabel}
          >
            {fileName}
          </Typography>
        </Box>
        {csvError
          ? (
            <Typography
              color="error"
              variant="subtitle2"
            >
              {csvError}
            </Typography>
          )
          : null
        }
        {checkpoints.length > 0
          ? (
            <Typography
              color="primary"
              variant="subtitle2"
            >
              {formatMessage(messages['checkpoint.bulkImport.found'], {count: checkpoints.length})}
            </Typography>
          )
          : null
        }
        <div>
          {formatMessage(messages['checkpoint.bulkImport.helperText.header'])}
          <ul>
            <li>
              {formatMessage(
                messages['checkpoint.bulkImport.helperText.1'],
                {code: ((...chunks: any[]) => <code>{chunks}</code>)}
              )}
            </li>
            <li>{formatMessage(messages['checkpoint.bulkImport.helperText.2'])}</li>
            <li>{formatMessage(messages['checkpoint.bulkImport.helperText.3'])}</li>
          </ul>
        </div>
      </FormPanel>
      <FormPanel
        heading={formatMessage(messages['checkpoint.bulkImport.formHeader.type'])}
        name="checkpoint"
        initialExpanded={true}
      >
        <CheckpointTypeAutocomplete
          multiple={true}
          initialValue={typeValue}
          className={classes.formInput}
          onChange={onTypeChange}
          fullWidth={true}
        />
      </FormPanel>
      <FormPanel
        heading={formatMessage(messages['checkpoint.bulkImport.formHeader.location'])}
        name="checkpoint"
        initialExpanded={true}
      >
        <LocationAutocomplete
          onChange={onLocationChange}
          initialValue={undefined}
          className={classes.formInput}
          fullWidth={true}
        />
      </FormPanel>
      <Grid container={true} alignItems="flex-start" justify="flex-end" direction="row">
        <Button
          variant="contained"
          color="secondary"
          component={MaterialUiLink}
          to={previousLocation}
          className={classes.submitButton}
          size="large"
        >
          <CancelIcon/>
          {formatMessage(appMessages['app.cancel'])}
        </Button>
        <Button
          disabled={!readyToImport}
          variant="contained"
          component="label"
          color="primary"
          size="large"
          className={classes.submitButton}
          onClick={handleImportClick}
        >
          {formatMessage(messages['checkpoint.bulkImport.formHeader.import'])}
        </Button>
      </Grid>
      <RouteLeavingGuard when={dirty} />
    </form>
  );

};

export default BulkImportCheckpointsForm;
